import React, { useState, useRef, useEffect } from "react";
import { BsTrash, BsPencil } from "react-icons/bs";
import axios from 'axios';
import prescriptionService from '../services/prescriptionService';
import MultiCreatePrescription from "./MultiCreatePrescription";
import { 
  API_BASE_URL, 
  API_PATIENT_LIST, 
  API_DOCTOR_LIST, 
  API_INVENTORY,
  API_BASE_HEALTH_CENTER_URL,
  API_HEALTH_CENTERS,
  API_TREATMENTM_LIST,
  API_LAB_TEMPLATES_LIST,
  buildQueryString
} from '../../../helpers/urlHelper';


// Estados iniciales actualizados según el nuevo modelo
const EMPTY_PRESCRIPTION_STATE = {
  doctorId: '',
  patientId: '',
  clinicId: '',
  symptoms: '',
  suggestion: '',
  condition: {
    conditionname: '',
    currentstatus: 'ongoing',
    reportedon: new Date().toISOString().split('T')[0],
    resolvedon: '',
    comments: ''
  },
  diseases: [],
  medicines: [],
  labtests: []
};

const INITIAL_DISEASE_STATE = {
  diseaseId: '',
  name: '',
  description: '',
  severity: 'moderate', // low, moderate, high
  duration: ''
};

const INITIAL_MEDICINE_STATE = {
  medicineId: '',
  name: '',
  dosage: '',
  frequency: '',
  duration: '',
  purpose: ''
};

const INITIAL_LABTEST_STATE = {
  labtestId: '',
  name: '',
  result: '',
  date: new Date().toISOString().split('T')[0]
};

// Componentes auxiliares
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center my-3">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Cargando...</span>
    </div>
  </div>
);

const AlertMessage = ({ type, message }) => (
  <div className={`alert alert-${type}`} role="alert">
    {message}
  </div>
);

const ConditionSection = ({ prescription, handleConditionChange }) => (
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">Condición Médica</h6>
    
    <div className="row">
      <div className="col-md-6 mb-3">
        <label htmlFor="conditionName" className="form-label">Nombre de la Condición</label>
        <input
          type="text"
          className="form-control"
          id="conditionName"
          name="conditionname"
          value={prescription.condition.conditionname}
          onChange={handleConditionChange}
          placeholder="ej. Hipertensión arterial, Diabetes tipo 2"
        />
      </div>
      <div className="col-md-6 mb-3">
        <label htmlFor="conditionStatus" className="form-label">Estado Actual</label>
        <select
          className="form-select"
          id="conditionStatus"
          name="currentstatus"
          value={prescription.condition.currentstatus}
          onChange={handleConditionChange}
        >
          <option value="ongoing">En curso</option>
          <option value="resolved">Resuelto</option>
          <option value="observation">En observación</option>
        </select>
      </div>
    </div>

    <div className="row">
      <div className="col-md-6 mb-3">
        <label htmlFor="conditionReportedOn" className="form-label">Fecha de Reporte</label>
        <input
          type="date"
          className="form-control"
          id="conditionReportedOn"
          name="reportedon"
          value={prescription.condition.reportedon}
          onChange={handleConditionChange}
        />
      </div>
      <div className="col-md-6 mb-3">
        <label htmlFor="conditionResolvedOn" className="form-label">Fecha de Resolución</label>
        <input
          type="date"
          className="form-control"
          id="conditionResolvedOn"
          name="resolvedon"
          value={prescription.condition.resolvedon || ''}
          onChange={handleConditionChange}
          disabled={prescription.condition.currentstatus !== 'resolved'}
        />
      </div>
    </div>

    <div className="mb-3">
      <label htmlFor="conditionComments" className="form-label">Comentarios</label>
      <textarea
        className="form-control"
        id="conditionComments"
        name="comments"
        value={prescription.condition.comments}
        onChange={handleConditionChange}
        rows="3"
        placeholder="Comentarios adicionales sobre la condición médica"
      />
    </div>
  </div>
);

const BasicDetailsSection = ({ prescription, handleInputChange, patients, doctors, clinics, medicalConditions }) => (
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">Detalles Básicos</h6>
    <div className="row">
      <div className="col-md-6 mb-3">
        <label htmlFor="patientId" className="form-label">Paciente *</label>
        <select
          className="form-select"
          id="patientId"
          name="patientId"
          value={prescription.patientId}
          onChange={handleInputChange}
          required
        >
          <option value="">Seleccionar paciente</option>
          {patients.map(patient => (
            <option key={patient._id} value={patient._id}>
              {patient.name} {patient.surname || patient.lastName || ''}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-6 mb-3">
        <label htmlFor="doctorId" className="form-label">Médico *</label>
        <select
          className="form-select"
          id="doctorId"
          name="doctorId"
          value={prescription.doctorId}
          onChange={handleInputChange}
          required
        >
          <option value="">Seleccionar médico</option>
          {doctors.map(doctor => (
            <option key={doctor._id} value={doctor._id}>
              Dr. {doctor.name} {doctor.surname || doctor.lastName || ''}
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className="row">
      <div className="col-md-6 mb-3">
        <label htmlFor="clinicId" className="form-label">Clínica *</label>
        <select
          className="form-select"
          id="clinicId"
          name="clinicId"
          value={prescription.clinicId}
          onChange={handleInputChange}
          required
        >
          <option value="">Seleccionar clínica</option>
          {clinics.map(clinic => (
            <option key={clinic._id} value={clinic._id}>
              {clinic.name}
            </option>
          ))}
        </select>
      </div>
      
    </div>
  </div>
);

const SymptomsSection = ({ prescription, handleInputChange }) => (
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">Síntomas y Sugerencias</h6>
    <div className="mb-3">
      <label htmlFor="symptoms" className="form-label">Síntomas</label>
      <textarea
        className="form-control"
        id="symptoms"
        name="symptoms"
        value={prescription.symptoms}
        onChange={handleInputChange}
        rows="3"
        placeholder="Describir síntomas del paciente"
      />
    </div>
    <div className="mb-3">
      <label htmlFor="suggestion" className="form-label">Sugerencias</label>
      <textarea
        className="form-control"
        id="suggestion"
        name="suggestion"
        value={prescription.suggestion}
        onChange={handleInputChange}
        rows="3"
        placeholder="Sugerencias médicas"
      />
    </div>
  </div>
);

const DiseasesSection = ({ 
  prescription, 
  disease, 
  handleDiseaseChange, 
  addDisease, 
  editDisease, 
  removeDisease, 
  editingDiseaseId,
  diseases 
}) => (
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">Enfermedades</h6>
    
    <div className="bg-light p-3 rounded mb-3">
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="diseaseId" className="form-label">Enfermedad</label>
          <select
  className="form-select"
  id="diseaseId"
  name="diseaseId"
  value={disease.diseaseId}
  onChange={handleDiseaseChange}
>
  <option value="">Seleccionar enfermedad</option>
  {diseases.map(d => (
    <option key={d._id} value={d._id}>
      {d.name || d.diseaseName || d.template?.name || 'Sin nombre disponible'}
    </option>
  ))}
</select>
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="diseaseName" className="form-label">Nombre</label>
          <input
            type="text"
            className="form-control"
            id="diseaseName"
            name="name"
            placeholder="Nombre de la enfermedad"
            value={disease.name}
            onChange={handleDiseaseChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="diseaseSeverity" className="form-label">Severidad</label>
          <select
            className="form-select"
            id="diseaseSeverity"
            name="severity"
            value={disease.severity}
            onChange={handleDiseaseChange}
          >
            <option value="low">Baja</option>
            <option value="moderate">Moderada</option>
            <option value="high">Alta</option>
          </select>
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="diseaseDuration" className="form-label">Duración</label>
          <input
            type="text"
            className="form-control"
            id="diseaseDuration"
            name="duration"
            placeholder="ej. 1 semana, 1 mes"
            value={disease.duration}
            onChange={handleDiseaseChange}
          />
        </div>
      </div>
      <div className="mb-3">
        <label htmlFor="diseaseDescription" className="form-label">Descripción</label>
        <textarea
          className="form-control"
          id="diseaseDescription"
          name="description"
          rows="2"
          placeholder="Descripción de la enfermedad"
          value={disease.description}
          onChange={handleDiseaseChange}
        />
      </div>
      <button 
        className="btn btn-primary" 
        onClick={addDisease}
        type="button"
      >
        {editingDiseaseId !== null ? 'Actualizar Enfermedad' : 'Agregar Enfermedad'}
      </button>
    </div>

    {prescription.diseases.length > 0 && (
      <div className="table-responsive">
        <table className="table table-striped table-sm">
          <thead className="table-info">
            <tr>
              <th>Enfermedad</th>
              <th>Severidad</th>
              <th>Duración</th>
              <th>Descripción</th>
              <th className="text-center" style={{ width: '120px' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {prescription.diseases.map((d) => (
              <tr key={d.id}>
                <td>{d.name}</td>
                <td>
                  <span className={`badge ${
                    d.severity === 'high' ? 'bg-danger' : 
                    d.severity === 'moderate' ? 'bg-warning' : 'bg-success'
                  }`}>
                    {d.severity === 'high' ? 'Alta' : 
                     d.severity === 'moderate' ? 'Moderada' : 'Baja'}
                  </span>
                </td>
                <td>{d.duration || '-'}</td>
                <td>{d.description || '-'}</td>
                <td className="text-center">
                  <button 
                    className="btn btn-warning btn-sm me-1"
                    onClick={() => editDisease(d.id)}
                    type="button"
                  >
                    <BsPencil />
                  </button>
                  <button 
                    className="btn btn-danger btn-sm"
                    onClick={() => removeDisease(d.id)}
                    type="button"
                  >
                    <BsTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const MedicinesSection = ({
  prescription,
  medicine,
  handleMedicineChange,
  addMedicine,
  editMedicine,
  removeMedicine,
  editingMedicineId,
  medicines,
  setMedicine
}) => (
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">Medicamentos</h6>
    
    <div className="bg-light p-3 rounded mb-3">
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="medicineId" className="form-label">Medicamento</label>
          <select
            className="form-select"
            id="medicineId"
            name="medicineId"
            value={medicine.medicineId || ''}
            onChange={(e) => {
              const medicineId = e.target.value;
              const selectedMedicine = medicines.find(m => m._id === medicineId);
              setMedicine(prev => ({ 
                ...prev, 
                medicineId: medicineId,
                name: selectedMedicine ? selectedMedicine.name || selectedMedicine.medicineName : ''
              }));
            }}
          >
            <option value="">Seleccionar medicamento</option>
            {medicines.map(med => (
              <option key={med._id} value={med._id}>
                {med.name || med.medicineName}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="medicineName" className="form-label">Nombre</label>
          <input
            type="text"
            className="form-control"
            id="medicineName"
            name="name"
            placeholder="Nombre del medicamento"
            value={medicine.name}
            onChange={handleMedicineChange}
          />
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="medicineDosage" className="form-label">Dosis</label>
          <input
            type="text"
            className="form-control"
            id="medicineDosage"
            name="dosage"
            placeholder="ej. 500mg"
            value={medicine.dosage || ''}
            onChange={handleMedicineChange}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="medicineFrequency" className="form-label">Frecuencia</label>
          <input
            type="text"
            className="form-control"
            id="medicineFrequency"
            name="frequency"
            placeholder="ej. Cada 8 horas"
            value={medicine.frequency || ''}
            onChange={handleMedicineChange}
          />
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="medicineDuration" className="form-label">Duración</label>
          <input
            type="text"
            className="form-control"
            id="medicineDuration"
            name="duration"
            placeholder="ej. 7 días"
            value={medicine.duration || ''}
            onChange={handleMedicineChange}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="medicinePurpose" className="form-label">Propósito</label>
          <input
            type="text"
            className="form-control"
            id="medicinePurpose"
            name="purpose"
            placeholder="Propósito del medicamento"
            value={medicine.purpose || ''}
            onChange={handleMedicineChange}
          />
        </div>
      </div>
      
      <button 
        className="btn btn-primary" 
        onClick={addMedicine}
        type="button"
      >
        {editingMedicineId !== null ? 'Actualizar Medicamento' : 'Agregar Medicamento'}
      </button>
    </div>

    {prescription.medicines.length > 0 && (
      <div className="table-responsive">
        <table className="table table-striped table-sm">
          <thead className="table-info">
            <tr>
              <th>Medicamento</th>
              <th>Dosis</th>
              <th>Frecuencia</th>
              <th>Duración</th>
              <th>Propósito</th>
              <th className="text-center" style={{ width: '120px' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {prescription.medicines.map((m) => (
              <tr key={m.id}>
                <td>{m.name}</td>
                <td>{m.dosage || '-'}</td>
                <td>{m.frequency || '-'}</td>
                <td>{m.duration || '-'}</td>
                <td>{m.purpose || '-'}</td>
                <td className="text-center">
                  <button 
                    className="btn btn-warning btn-sm me-1"
                    onClick={() => editMedicine(m.id)}
                    type="button"
                  >
                    <BsPencil />
                  </button>
                  <button 
                    className="btn btn-danger btn-sm"
                    onClick={() => removeMedicine(m.id)}
                    type="button"
                  >
                    <BsTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const LabTestsSection = ({ 
  prescription, 
  labtest, 
  handleLabTestChange, 
  addLabTest, 
  editLabTest, 
  removeLabTest, 
  editingLabTestId,
  labtests,
  setLabTest
}) => (
  <div className="mb-4">
    <h6 className="text-primary border-bottom pb-2 mb-3">Exámenes de Laboratorio</h6>
    
    <div className="bg-light p-3 rounded mb-3">
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="labtestId" className="form-label">Examen</label>
          <select
            className="form-select"
            id="labtestId"
            name="labtestId"
            value={labtest.labtestId || ''}
            onChange={(e) => {
              const labtestId = e.target.value;
              const selectedLabTest = labtests.find(l => l._id === labtestId);
              setLabTest(prev => ({ 
                ...prev, 
                labtestId: labtestId,
                name: selectedLabTest ? selectedLabTest.name || selectedLabTest.testName : ''
              }));
            }}
          >
            <option value="">Seleccionar examen</option>
            {labtests.map(lab => (
              <option key={lab._id} value={lab._id}>
                {lab.name || lab.testName}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="labtestName" className="form-label">Nombre</label>
          <input
            type="text"
            className="form-control"
            id="labtestName"
            name="name"
            placeholder="Nombre del examen"
            value={labtest.name}
            onChange={handleLabTestChange}
          />
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="labtestResult" className="form-label">Resultado</label>
          <input
            type="text"
            className="form-control"
            id="labtestResult"
            name="result"
            placeholder="Resultado del examen (opcional)"
            value={labtest.result || ''}
            onChange={handleLabTestChange}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="labtestDate" className="form-label">Fecha</label>
          <input
            type="date"
            className="form-control"
            id="labtestDate"
            name="date"
            value={labtest.date}
            onChange={handleLabTestChange}
          />
        </div>
      </div>
      
      <button 
        className="btn btn-primary" 
        onClick={addLabTest}
        type="button"
      >
        {editingLabTestId !== null ? 'Actualizar Examen' : 'Agregar Examen'}
      </button>
    </div>

    {prescription.labtests.length > 0 && (
      <div className="table-responsive">
        <table className="table table-striped table-sm">
          <thead className="table-info">
            <tr>
              <th>Examen</th>
              <th>Resultado</th>
              <th>Fecha</th>
              <th className="text-center" style={{ width: '120px' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {prescription.labtests.map((l) => (
              <tr key={l.id}>
                <td>{l.name}</td>
                <td>{l.result || 'Pendiente'}</td>
                <td>{new Date(l.date).toLocaleDateString()}</td>
                <td className="text-center">
                  <button 
                    className="btn btn-warning btn-sm me-1"
                    onClick={() => editLabTest(l.id)}
                    type="button"
                  >
                    <BsPencil />
                  </button>
                  <button 
                    className="btn btn-danger btn-sm"
                    onClick={() => removeLabTest(l.id)}
                    type="button"
                  >
                    <BsTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// Componente principal con botón incluido
const CreatePrescriptionOffcanvas = () => {
  const offcanvasCloseRef = useRef(null);

  // Estados principales
  const [showMultiCreate, setShowMultiCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Estados de datos
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [labtests, setLabtests] = useState([]);

  // Estados del formulario
  const [prescription, setPrescription] = useState(EMPTY_PRESCRIPTION_STATE);
  const [disease, setDisease] = useState(INITIAL_DISEASE_STATE);
  const [medicine, setMedicine] = useState(INITIAL_MEDICINE_STATE);
  const [labtest, setLabTest] = useState(INITIAL_LABTEST_STATE);
  
  const [editingDiseaseId, setEditingDiseaseId] = useState(null);
  const [editingMedicineId, setEditingMedicineId] = useState(null);
  const [editingLabTestId, setEditingLabTestId] = useState(null);

  // Funciones de API actualizadas
  const fetchDoctors = async () => {
    try {
      const response = await fetch(`${API_DOCTOR_LIST}`);
      if (!response.ok) throw new Error('Error al obtener médicos');
      const data = await response.json();
      return data?.data || [];
    } catch (err) {
      console.error('Error fetching doctors:', err);
      return [];
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_PATIENT_LIST}`);
      if (!response.ok) throw new Error('Error al obtener pacientes');
      const data = await response.json();
      return data?.data || [];
    } catch (err) {
      console.error('Error fetching patients:', err);
      return [];
    }
  };



  // Función actualizada para obtener medicinas desde inventory
  const fetchMedicines = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}${API_INVENTORY}`);
      return response.data || [];
    } catch (err) {
      console.error('Error fetching medicines:', err);
      return [];
    }
  };

  // Función actualizada para obtener centros de salud
// Función con más logging para debuggear
const fetchClinics = async () => {
  try {
    console.log('Fetching clinics from:', `${API_BASE_HEALTH_CENTER_URL}${API_HEALTH_CENTERS}`);
    const response = await fetch(`${API_BASE_HEALTH_CENTER_URL}${API_HEALTH_CENTERS}`);
    console.log('Clinics response status:', response.status);
    
    if (!response.ok) {
      console.error('Clinics fetch failed:', response.status, response.statusText);
      throw new Error(`Error al obtener clínicas: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Clinics raw response:', data);
    console.log('Clinics processed data:', data?.data || data || []);
    
    return data?.data || data || [];
  } catch (err) {
    console.error('Error fetching clinics:', err);
    return [];
  }
};

const fetchDiseases = async () => {
  try {
    console.log('Fetching diseases from:', `${API_BASE_URL}${API_TREATMENTM_LIST}`);
    const response = await fetch(`${API_BASE_URL}${API_TREATMENTM_LIST}`);
    console.log('Diseases response status:', response.status);
    
    if (!response.ok) {
      console.error('Diseases fetch failed:', response.status, response.statusText);
      throw new Error(`Error al obtener enfermedades: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Diseases raw response:', data);
    
    let processedData = [];
    
    // Caso 1: Si es un objeto con template directamente
    if (data.template && data.template.name) {
      processedData = [{
        _id: data._id || Date.now().toString(),
        name: data.template.name,
        diseaseName: data.template.name,
        purpose: data.template.purpose || '',
        protocol_code: data.template.protocol_code || '',
        specialty_id: data.template.specialty_id || '',
        status: data.template.status || ''
      }];
    }
    // Caso 2: Si es un array de objetos con template
    else if (Array.isArray(data)) {
      processedData = data.map(item => ({
        _id: item._id || Date.now().toString(),
        name: item.template?.name || item.name || item.diseaseName || 'Sin nombre',
        diseaseName: item.template?.name || item.diseaseName || item.name || 'Sin nombre',
        purpose: item.template?.purpose || item.purpose || '',
        protocol_code: item.template?.protocol_code || item.protocol_code || '',
        specialty_id: item.template?.specialty_id || item.specialty_id || '',
        status: item.template?.status || item.status || ''
      }));
    }
    // Caso 3: Si tiene data como array
    else if (data?.data && Array.isArray(data.data)) {
      processedData = data.data.map(item => ({
        _id: item._id || Date.now().toString(),
        name: item.template?.name || item.name || item.diseaseName || 'Sin nombre',
        diseaseName: item.template?.name || item.diseaseName || item.name || 'Sin nombre',
        purpose: item.template?.purpose || item.purpose || '',
        protocol_code: item.template?.protocol_code || item.protocol_code || '',
        specialty_id: item.template?.specialty_id || item.specialty_id || '',
        status: item.template?.status || item.status || ''
      }));
    }
    // Caso 4: Respuesta directa con data que contiene template
    else if (data?.data?.template?.name) {
      processedData = [{
        _id: data.data._id || data._id || Date.now().toString(),
        name: data.data.template.name,
        diseaseName: data.data.template.name,
        purpose: data.data.template.purpose || '',
        protocol_code: data.data.template.protocol_code || '',
        specialty_id: data.data.template.specialty_id || '',
        status: data.data.template.status || ''
      }];
    }
    
    console.log('Diseases processed data:', processedData);
    return processedData;
    
  } catch (err) {
    console.error('Error fetching diseases:', err);
    return [];
  }
};

  // Función para obtener exámenes de laboratorio
  const fetchLabTests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_LAB_TEMPLATES_LIST}`);
      if (!response.ok) throw new Error('Error al obtener exámenes de laboratorio');
      const data = await response.json();
      return data?.data || [];
    } catch (err) {
      console.error('Error fetching lab tests:', err);
      return [];
    }
  };

  // Funciones de utilidad
  const resetOffcanvas = () => {
    setPrescription(EMPTY_PRESCRIPTION_STATE);
    setDisease(INITIAL_DISEASE_STATE);
    setMedicine(INITIAL_MEDICINE_STATE);
    setLabTest(INITIAL_LABTEST_STATE);
    setEditingDiseaseId(null);
    setEditingMedicineId(null);
    setEditingLabTestId(null);
    setError(null);
    setSuccessMessage(null);
  };

  const validateForm = () => {
  if (!prescription.patientId) {
    setError('Por favor selecciona un paciente');
    return false;
  }
  if (!prescription.doctorId) {
    setError('Por favor selecciona un médico');
    return false;
  }
  if (!prescription.clinicId) {
    setError('Por favor selecciona una clínica');
    return false;
  }
  
  // NUEVO: Validación adicional para arrays
  if (prescription.diseases.some(d => !d.name)) {
    setError('Todas las enfermedades deben tener un nombre');
    return false;
  }
  
  if (prescription.medicines.some(m => !m.name)) {
    setError('Todos los medicamentos deben tener un nombre');
    return false;
  }
  
  if (prescription.labtests.some(l => !l.name)) {
    setError('Todos los exámenes deben tener un nombre');
    return false;
  }
  
  return true;
};

  // Función para inicializar el offcanvas
  const handleOffcanvasInit = () => {
    resetOffcanvas();
  };

  // Manejadores de eventos
  const handleInputChange = React.useCallback((e) => {
    const { name, value } = e.target;
    setPrescription(prev => ({ ...prev, [name]: value }));
  }, []);

const handleDiseaseChange = React.useCallback((e) => {
  const { name, value } = e.target;
  
  if (name === 'diseaseId' && value) {
    const selectedDisease = diseases.find(d => d._id === value);
    if (selectedDisease) {
      setDisease(prev => ({ 
        ...prev, 
        diseaseId: value,
        name: selectedDisease.name || selectedDisease.diseaseName || selectedDisease.template?.name || ''
      }));
      return;
    }
  }
  
  setDisease(prev => ({ ...prev, [name]: value }));
}, [diseases]);

  const handleMedicineChange = React.useCallback((e) => {
    const { name, value } = e.target;
    setMedicine(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleLabTestChange = React.useCallback((e) => {
    const { name, value } = e.target;
    setLabTest(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleConditionChange = React.useCallback((e) => {
  const { name, value } = e.target;
  setPrescription(prev => ({
    ...prev,
    condition: {
      ...prev.condition,
      [name]: value
    }
  }));
}, []);

  // Funciones para manejar enfermedades
  const addDisease = React.useCallback(() => {
    if (!disease.name && !disease.diseaseId) {
      alert('Por favor completa el nombre de la enfermedad');
      return;
    }

    if (editingDiseaseId !== null) {
      setPrescription(prev => ({
        ...prev,
        diseases: prev.diseases.map(d => 
          d.id === editingDiseaseId ? { ...disease, id: editingDiseaseId } : d
        )
      }));
      setEditingDiseaseId(null);
    } else {
      setPrescription(prev => ({
        ...prev,
        diseases: [...prev.diseases, { ...disease, id: Date.now() }]
      }));
    }
    setDisease(INITIAL_DISEASE_STATE);
  }, [disease, editingDiseaseId]);

  const editDisease = React.useCallback((id) => {
    const diseaseToEdit = prescription.diseases.find(d => d.id === id);
    if (diseaseToEdit) {
      setDisease(diseaseToEdit);
      setEditingDiseaseId(id);
    }
  }, [prescription.diseases]);

  const removeDisease = React.useCallback((id) => {
    setPrescription(prev => ({
      ...prev,
      diseases: prev.diseases.filter(d => d.id !== id)
    }));
    if (editingDiseaseId === id) {
      setDisease(INITIAL_DISEASE_STATE);
      setEditingDiseaseId(null);
    }
  }, [editingDiseaseId]);

  // Funciones para manejar medicamentos
  const addMedicine = React.useCallback(() => {
    if (!medicine.name) {
      alert('Por favor completa el nombre del medicamento');
      return;
    }

    if (editingMedicineId !== null) {
      setPrescription(prev => ({
        ...prev,
        medicines: prev.medicines.map(m => 
          m.id === editingMedicineId ? { ...medicine, id: editingMedicineId } : m
        )
      }));
      setEditingMedicineId(null);
    } else {
      setPrescription(prev => ({
        ...prev,
        medicines: [...prev.medicines, { ...medicine, id: Date.now() }]
      }));
    }
    setMedicine(INITIAL_MEDICINE_STATE);
  }, [medicine, editingMedicineId]);

  const editMedicine = React.useCallback((id) => {
    const medicineToEdit = prescription.medicines.find(m => m.id === id);
    if (medicineToEdit) {
      setMedicine(medicineToEdit);
      setEditingMedicineId(id);
    }
  }, [prescription.medicines]);

  const removeMedicine = React.useCallback((id) => {
    setPrescription(prev => ({
      ...prev,
      medicines: prev.medicines.filter(m => m.id !== id)
    }));
    if (editingMedicineId === id) {
      setMedicine(INITIAL_MEDICINE_STATE);
      setEditingMedicineId(null);
    }
  }, [editingMedicineId]);

  // Funciones para manejar exámenes de laboratorio
  const addLabTest = React.useCallback(() => {
    if (!labtest.name) {
      alert('Por favor completa el nombre del examen');
      return;
    }

    if (editingLabTestId !== null) {
      // Continuación exacta desde donde se cortó tu código:
      setPrescription(prev => ({
        ...prev,
        labtests: prev.labtests.map(l => 
          l.id === editingLabTestId ? { ...labtest, id: editingLabTestId } : l
        )
      }));
      setEditingLabTestId(null);
    } else {
      setPrescription(prev => ({
        ...prev,
        labtests: [...prev.labtests, { ...labtest, id: Date.now() }]
      }));
    }
    setLabTest(INITIAL_LABTEST_STATE);
  }, [labtest, editingLabTestId]);

  const editLabTest = React.useCallback((id) => {
    const labtestToEdit = prescription.labtests.find(l => l.id === id);
    if (labtestToEdit) {
      setLabTest(labtestToEdit);
      setEditingLabTestId(id);
    }
  }, [prescription.labtests]);

  const removeLabTest = React.useCallback((id) => {
    setPrescription(prev => ({
      ...prev,
      labtests: prev.labtests.filter(l => l.id !== id)
    }));
    if (editingLabTestId === id) {
      setLabTest(INITIAL_LABTEST_STATE);
      setEditingLabTestId(null);
    }
  }, [editingLabTestId]);

  // Función principal para crear prescripción
const createPrescription = async () => {
  if (!validateForm()) return;

  setLoading(true);
  setError(null);
  
  try {
    const prescriptionData = {
      // CAMBIO: Mapear correctamente los IDs
      doctor: prescription.doctorId,
      patient: prescription.patientId,
      clinic: prescription.clinicId,
      symptoms: prescription.symptoms,
      suggestion: prescription.suggestion,
      condition: prescription.condition, // Esto puede ser null/undefined
      // CAMBIO: Formatear diseases correctamente
      diseases: prescription.diseases.map(disease => ({
        disease: disease.diseaseId || null, // Referencia opcional
        name: disease.name,
        description: disease.description,
        severity: disease.severity,
        duration: disease.duration
      })),
      // CAMBIO: Formatear medicines correctamente
      medicines: prescription.medicines.map(medicine => ({
        medicine: medicine.medicineId || null, // Referencia opcional
        name: medicine.name,
        dosage: medicine.dosage,
        frequency: medicine.frequency,
        duration: medicine.duration,
        purpose: medicine.purpose
      })),
      // CAMBIO: Formatear labtests correctamente
      labtests: prescription.labtests.map(labtest => ({
        labtest: labtest.labtestId || null, // Referencia opcional
        name: labtest.name,
        result: labtest.result,
        date: labtest.date
      }))
    };

    console.log('Datos a enviar:', prescriptionData); // Para debug

    await prescriptionService.createPrescription(prescriptionData);
    
    setSuccessMessage('Prescripción creada exitosamente');
    
    setTimeout(() => {
      resetOffcanvas();
      if (offcanvasCloseRef.current) {
        offcanvasCloseRef.current.click();
      }
      window.location.reload();
    }, 2000);
  } catch (err) {
    console.error('Error creating prescription:', err);
    setError(err.response?.data?.message || err.message || 'Error al crear la prescripción');
  } finally {
    setLoading(false);
  }
};

  // Cargar datos iniciales cuando el offcanvas se abre
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [
          doctorsData,
          patientsData,
          medicinesData,
          clinicsData,
          diseasesData,
          labtestsData
        ] = await Promise.all([
          fetchDoctors(),
          fetchPatients(),
          fetchMedicines(),
          fetchClinics(),
          fetchDiseases(),
          fetchLabTests()
        ]);
        setDoctors(doctorsData);
        setPatients(patientsData);
        setMedicines(medicinesData);
        setClinics(clinicsData);
        setDiseases(diseasesData);
        setLabtests(labtestsData);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Error al cargar los datos iniciales');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  return (
    <>
      <button 
        className="btn btn-primary addnewbtn d-flex align-items-center gap-1" 
        type="button" 
        data-bs-toggle="offcanvas" 
        data-bs-target="#createPrescriptionOffcanvas"
        onClick={handleOffcanvasInit}
      >
        <span className="d-block material-symbols-outlined icon-md">add</span>
                                        <span className="d-block">Add New</span>
      </button>

      <div 
        className="offcanvas offcanvas-end" 
        tabIndex="-1" 
        id="createPrescriptionOffcanvas"
        style={{ width: '900px' }}
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">
            <i className="bi bi-file-medical me-2 text-primary"></i>
            Nueva Prescripción Médica
          </h5>
          <div>
            <button 
        className="btn btn-outline-secondary btn-sm me-2"
        onClick={() => setShowMultiCreate(true)}
        type="button"
      >
        <i className="bi bi-files me-1"></i>
        Multi
      </button>
            <button 
              type="button" 
              className="btn-close" 
              data-bs-dismiss="offcanvas"
              ref={offcanvasCloseRef}
            ></button>
          </div>
        </div>

        <div className="offcanvas-body">
          {loading && <LoadingSpinner />}
          
          {error && <AlertMessage type="danger" message={error} />}
          {successMessage && <AlertMessage type="success" message={successMessage} />}

          {!loading && (
            <form onSubmit={(e) => { e.preventDefault(); createPrescription(); }}>
              <BasicDetailsSection
                prescription={prescription}
                handleInputChange={handleInputChange}
                patients={patients}
                doctors={doctors}
                clinics={clinics}
              />

              <ConditionSection
  prescription={prescription}
  handleConditionChange={handleConditionChange}
/>

              <SymptomsSection
                prescription={prescription}
                handleInputChange={handleInputChange}
              />

              <DiseasesSection
                prescription={prescription}
                disease={disease}
                handleDiseaseChange={handleDiseaseChange}
                addDisease={addDisease}
                editDisease={editDisease}
                removeDisease={removeDisease}
                editingDiseaseId={editingDiseaseId}
                diseases={diseases}
              />

              <MedicinesSection
                prescription={prescription}
                medicine={medicine}
                handleMedicineChange={handleMedicineChange}
                addMedicine={addMedicine}
                editMedicine={editMedicine}
                removeMedicine={removeMedicine}
                editingMedicineId={editingMedicineId}
                medicines={medicines}
                setMedicine={setMedicine}
              />

              <LabTestsSection
                prescription={prescription}
                labtest={labtest}
                handleLabTestChange={handleLabTestChange}
                addLabTest={addLabTest}
                editLabTest={editLabTest}
                removeLabTest={removeLabTest}
                editingLabTestId={editingLabTestId}
                labtests={labtests}
                setLabTest={setLabTest}
              />

              <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={resetOffcanvas}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Limpiar
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      Crear Prescripción
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {showMultiCreate && (
        <MultiCreatePrescription 
          isOpen={showMultiCreate}
          onClose={() => setShowMultiCreate(false)}
        />
      )}
    </>
  );
};

export default CreatePrescriptionOffcanvas;